<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="d-flex align-items-center">
          <button @click="back" class="btn btn-outline-secondary btn-icon-text py-2 px-3 mr-3">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <div>
            <h2 class="text-white font-weight-bold mb-1">{{ $t('rulesForm.editConsequenceTitle') }}</h2>
            <p class="text-muted small mb-0">{{ $t('rulesForm.editConsequenceSub') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card border border-secondary">
        <div class="card-body">
          <form class="forms-sample" method="post" @submit.prevent="submitForm">
            <div class="form-group row">
              <div class="col-md-6 mb-3">
                <label for="nameInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('rulesForm.consequenceName') }}</label>
                <input
                  id="nameInput"
                  type="text"
                  class="form-control"
                  v-model="ruleStore.name"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Terlambat Menghadiri Rapat Harian' : 'e.g. Late for Daily Standup Meeting'"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="punishmentInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('rulesForm.punishmentType') }}</label>
                <select class="form-control" id="punishmentInput" v-model="ruleStore.punishment">
                  <option value="gold">{{ $t('rulesForm.penaltyGold') }} (-Gold)</option>
                  <option value="non_gold">{{ $t('rulesForm.penaltyNonGold') }}</option>
                </select>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-6 mb-3" v-if="ruleStore.punishment === 'gold'">
                <label for="goldInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('quests.pointsDeduction') }}</label>
                <div class="input-group">
                  <input
                    id="goldInput"
                    type="number"
                    class="form-control"
                    v-model="ruleStore.gold"
                    min="1"
                    placeholder="10"
                    required
                  />
                  <div class="input-group-append">
                    <span class="input-group-text bg-dark border-secondary text-danger font-weight-bold">
                      <i class="mdi mdi-minus-circle mr-1"></i> Gold
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3" v-else>
                <label for="nonGoldInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('rulesForm.penaltyNonGold') }}</label>
                <textarea
                  class="form-control"
                  id="nonGoldInput"
                  rows="2"
                  v-model="ruleStore.non_gold"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Berikan sesi berbagi santai 10 menit' : 'e.g. Deliver a 10-minute casual knowledge sharing session'"
                  required
                ></textarea>
              </div>
              <div class="col-md-6 mb-3">
                <label for="descriptionInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('common.description') }}</label>
                <textarea
                  class="form-control"
                  id="descriptionInput"
                  rows="2"
                  v-model="ruleStore.description"
                  :placeholder="$currentLocale === 'id' ? 'Jelaskan kriteria pelanggaran SOP ini...' : 'Describe the criteria for this SOP violation...'"
                ></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button type="button" @click="back" class="btn btn-outline-secondary px-4 py-2 mr-2" :disabled="loading">
                {{ $t('common.back') }}
              </button>
              <button type="submit" class="btn btn-primary px-4 py-2 font-weight-bold" :disabled="loading">
                <span class="spinner-border spinner-border-sm mr-1" v-if="loading" role="status"></span>
                <i class="mdi mdi-check-all mr-1" v-else></i>
                {{ $t('common.save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDetailConsequence, updateConsequence } from "../../services/consequenceService";
import Swal from 'sweetalert2';

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    ruleStore: {
      name: '',
      gold: 0,
      punishment: 'gold',
      non_gold: '',
      description: '',
    },
    loading: false,
  }),
  watch: {
    'ruleStore.punishment': function (newVal) {
      if (newVal === 'gold') {
        this.ruleStore.non_gold = null;
      } else {
        this.ruleStore.gold = null;
      }
    }
  },
  methods: {
    back() {
      this.$router.go(-1);
    },
    async submitForm() {
      try {
        this.loading = true;
        await updateConsequence(this.id, this.ruleStore);
        Swal.fire({
          icon: "success",
          title: this.$t('common.success'),
          text: this.$t('rulesForm.updatedSuccess'),
          confirmButtonColor: "#3b82f6",
        });

        this.$router.push('/rules/consequences');
      } catch (error) {
        console.error(error);
        const errMsg = error.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal menyimpan perubahan aturan konsekuensi.' : 'Failed to update consequence rule.');
        Swal.fire({
          icon: 'error',
          title: this.$t('common.error') || (this.$currentLocale === 'id' ? 'Terjadi Kesalahan' : 'Error'),
          text: errMsg,
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchConsequence() {
      try {
        const response = await getDetailConsequence(this.id);
        const data = response.data;
        this.ruleStore.name = data.name || '';
        this.ruleStore.gold = data.gold;
        this.ruleStore.non_gold = data.non_gold;
        this.ruleStore.description = data.description || '';
        if (data.gold !== null && data.gold !== undefined) {
          this.ruleStore.punishment = 'gold';
        } else if (data.non_gold !== null) {
          this.ruleStore.punishment = 'non_gold';
        }
      } catch (err) {
        console.error('Error fetching consequence details:', err);
      }
    },
  },
  mounted() {
    this.fetchConsequence();
  }
};
</script>
